// app/frontend/pages/Dashboard.tsx
import { Box, Stack, Grid, Paper, Group, Progress, Image } from "@mantine/core";
import {
  IconPlayerPlay,
  IconEye,
  IconClock,
  IconFlame,
  IconBookmark,
  IconArrowRight,
  IconTrophy,
} from "@tabler/icons-react";
import { Link } from "@inertiajs/react";
import {
  L2KH2,
  L2KH3,
  L2KH4,
  L2KH5,
  L2KText,
  L2KCaption,
  L2KBadge,
} from "../components/ui/Typography";
import type { L2KBadgeVariant } from "../components/ui/Typography/types";
import VideoCard from "../components/videos/VideoCard";
import AuthenticatedLayout from "../components/layouts/AuthenticatedLayout";
import PageContainer from "../components/layouts/PageContainer";
import { SEO } from "../components/common/SEO";
import type { Video } from "../types/video";

interface User {
  id: number;
  email: string;
  name: string;
}

interface ContinueLearning {
  video: Video;
  track: {
    id: number;
    name: string;
  } | null;
}

interface Stats {
  videos_watched: number;
  watch_time_minutes: number;
  watch_time_formatted: string;
  streak_days: number;
}

interface LearningTrackProgress {
  id: number;
  name: string;
  description: string;
  total_videos: number;
  completed_videos: number;
  progress_percent: number;
  estimated_duration: string;
  difficulty_range: string;
}

interface Props {
  user: User;
  continue_learning: ContinueLearning | null;
  stats: Stats;
  bookmarked_videos: Video[];
  learning_tracks: LearningTrackProgress[];
}

export default function Dashboard({
  user,
  continue_learning,
  stats,
  bookmarked_videos,
  learning_tracks,
}: Props) {
  const handleVideoClick = (video: Video) => {
    window.location.href = `/videos/${video.slug}`;
  };

  const getDifficultyVariant = (range: string): L2KBadgeVariant => {
    const lower = range.toLowerCase();
    if (lower.includes("beginner")) return "beginner";
    if (lower.includes("intermediate")) return "intermediate";
    if (lower.includes("advanced")) return "advanced";
    return "muted";
  };

  return (
    <>
      <SEO
        title="Dashboard - Learntokiz"
        description="Your personalized learning dashboard"
      />
      <PageContainer>
        <Stack gap="xl" py="md">
          {/* Welcome Header */}
          <Box>
            <L2KH2 mb={4}>Welcome back, {user.name}</L2KH2>
            <L2KText variant="secondary">
              Continue your dance journey where you left off
            </L2KText>
          </Box>

          {/* Continue Learning Hero */}
          {continue_learning && (
            <Paper
              radius="lg"
              p={0}
              style={{
                background: "linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 152, 0, 0.05) 100%)",
                border: "1px solid rgba(255, 152, 0, 0.3)",
                overflow: "hidden",
              }}
            >
              <Grid gap={0}>
                <Grid.Col span={{ base: 12, md: 7 }}>
                  <Box style={{ position: "relative" }}>
                    <Box style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
                      <Image
                        src={continue_learning.video.thumbnail_url}
                        alt={continue_learning.video.title}
                        fallbackSrc="https://picsum.photos/1280/720?random=1"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {/* Play Button Overlay */}
                      <Link
                        href={`/videos/${continue_learning.video.slug}`}
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          background: "rgba(255, 152, 0, 0.9)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 20px rgba(255, 152, 0, 0.4)",
                        }}
                      >
                        <IconPlayerPlay size={36} color="#fff" style={{ marginLeft: "4px" }} />
                      </Link>
                    </Box>
                  </Box>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 5 }}>
                  <Stack gap="md" p="xl" h="100%" justify="center">
                    <Box>
                      <L2KCaption variant="accent" mb="xs">
                        Continue Learning
                      </L2KCaption>
                      <L2KH3 mb="xs">{continue_learning.video.title}</L2KH3>
                      {continue_learning.track && (
                        <L2KBadge variant="dot" size="md">
                          {continue_learning.track.name}
                        </L2KBadge>
                      )}
                    </Box>
                    <L2KText variant="secondary" lineClamp={3}>
                      {continue_learning.video.description}
                    </L2KText>
                    <Group gap="md">
                      <L2KBadge variant="muted" size="md">
                        {continue_learning.video.duration}
                      </L2KBadge>
                      <L2KBadge
                        variant={getDifficultyVariant(continue_learning.video.skill_level)}
                        size="md"
                      >
                        {continue_learning.video.skill_level}
                      </L2KBadge>
                    </Group>
                    <Link
                      href={`/videos/${continue_learning.video.slug}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "#FF9800",
                        textDecoration: "none",
                        fontFamily: "'Public Sans', sans-serif",
                        fontWeight: 600,
                        fontSize: "14px",
                        marginTop: "8px",
                      }}
                    >
                      Resume Video <IconArrowRight size={16} />
                    </Link>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Paper>
          )}

          {/* Quick Stats */}
          <Box>
            <L2KH4 mb="md">Your Progress</L2KH4>
            <Grid>
              <Grid.Col span={{ base: 12, xs: 4 }}>
                <Paper
                  radius="md"
                  p="lg"
                  style={{
                    background: "linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.05) 100%)",
                    border: "1px solid rgba(76, 175, 80, 0.3)",
                  }}
                >
                  <Group gap="md">
                    <Box
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: "rgba(76, 175, 80, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconEye size={24} color="#4CAF50" />
                    </Box>
                    <Box>
                      <L2KCaption variant="tertiary">Videos Watched</L2KCaption>
                      <L2KH3 style={{ color: "#4CAF50" }}>{stats.videos_watched}</L2KH3>
                    </Box>
                  </Group>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, xs: 4 }}>
                <Paper
                  radius="md"
                  p="lg"
                  style={{
                    background: "linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.05) 100%)",
                    border: "1px solid rgba(33, 150, 243, 0.3)",
                  }}
                >
                  <Group gap="md">
                    <Box
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: "rgba(33, 150, 243, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconClock size={24} color="#2196F3" />
                    </Box>
                    <Box>
                      <L2KCaption variant="tertiary">Watch Time</L2KCaption>
                      <L2KH3 style={{ color: "#2196F3" }}>{stats.watch_time_formatted}</L2KH3>
                    </Box>
                  </Group>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, xs: 4 }}>
                <Paper
                  radius="md"
                  p="lg"
                  style={{
                    background: "linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 152, 0, 0.05) 100%)",
                    border: "1px solid rgba(255, 152, 0, 0.3)",
                  }}
                >
                  <Group gap="md">
                    <Box
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: "rgba(255, 152, 0, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconFlame size={24} color="#FF9800" />
                    </Box>
                    <Box>
                      <L2KCaption variant="tertiary">Day Streak</L2KCaption>
                      <L2KH3 style={{ color: "#FF9800" }}>
                        {stats.streak_days} {stats.streak_days === 1 ? "day" : "days"}
                      </L2KH3>
                    </Box>
                  </Group>
                </Paper>
              </Grid.Col>
            </Grid>
          </Box>

          {/* Learning Tracks Progress */}
          {learning_tracks.length > 0 && (
            <Box>
              <Group justify="space-between" align="center" mb="md">
                <L2KH4 mb={0}>Learning Tracks</L2KH4>
                <Link
                  href="/videos"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: "#FF9800",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontFamily: "'Public Sans', sans-serif",
                  }}
                >
                  View all <IconArrowRight size={14} />
                </Link>
              </Group>
              <Grid>
                {learning_tracks.slice(0, 4).map((track) => (
                  <Grid.Col key={track.id} span={{ base: 12, sm: 6 }}>
                    <Link
                      href={`/videos?learning_track_id=${track.id}`}
                      style={{ textDecoration: "none" }}
                    >
                      <Paper
                        radius="md"
                        p="lg"
                        style={{
                          background: "rgba(255, 255, 255, 0.03)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          transition: "all 0.3s ease",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "rgba(255, 152, 0, 0.5)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        <Group justify="space-between" align="flex-start" mb="sm">
                          <Box style={{ flex: 1 }}>
                            <L2KH5 mb={4}>{track.name}</L2KH5>
                            <L2KCaption variant="tertiary" lineClamp={1}>
                              {track.description}
                            </L2KCaption>
                          </Box>
                          {track.progress_percent === 100 && (
                            <IconTrophy size={20} color="#FFD700" />
                          )}
                        </Group>
                        <Group justify="space-between" mb="xs">
                          <L2KCaption variant="secondary">
                            {track.completed_videos} of {track.total_videos} videos
                          </L2KCaption>
                          <L2KCaption variant="accent">
                            {track.progress_percent}%
                          </L2KCaption>
                        </Group>
                        <Progress
                          value={track.progress_percent}
                          size="sm"
                          radius="xl"
                          color="orange"
                          style={{
                            background: "rgba(255, 255, 255, 0.1)",
                          }}
                        />
                        <Group gap="xs" mt="sm">
                          <L2KBadge variant="muted" size="sm">
                            {track.estimated_duration}
                          </L2KBadge>
                          <L2KBadge variant={getDifficultyVariant(track.difficulty_range)} size="sm">
                            {track.difficulty_range}
                          </L2KBadge>
                        </Group>
                      </Paper>
                    </Link>
                  </Grid.Col>
                ))}
              </Grid>
            </Box>
          )}

          {/* Bookmarked Videos */}
          {bookmarked_videos.length > 0 && (
            <Box>
              <Group justify="space-between" align="center" mb="md">
                <Group gap="xs">
                  <IconBookmark size={20} color="#FF9800" />
                  <L2KH4 mb={0}>Bookmarked Videos</L2KH4>
                </Group>
                <Link
                  href="/videos/bookmarks"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: "#FF9800",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontFamily: "'Public Sans', sans-serif",
                  }}
                >
                  View all <IconArrowRight size={14} />
                </Link>
              </Group>
              <Grid>
                {bookmarked_videos.map((video) => (
                  <Grid.Col key={video.id} span={{ base: 12, xs: 6, sm: 4, md: 3 }}>
                    <VideoCard
                      video={video}
                      userVideo={{ bookmarked_at: "true", favorited_at: null, watched_status: null }}
                      onVideoClick={handleVideoClick}
                    />
                  </Grid.Col>
                ))}
              </Grid>
            </Box>
          )}

          {/* Empty State - No Activity */}
          {!continue_learning && bookmarked_videos.length === 0 && (
            <Paper
              radius="lg"
              p="xl"
              ta="center"
              style={{
                background: "linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.02) 100%)",
                border: "1px solid rgba(255, 152, 0, 0.2)",
              }}
            >
              <IconPlayerPlay size={48} color="#FF9800" style={{ marginBottom: "16px" }} />
              <L2KH3 mb="xs">Start Your Dance Journey</L2KH3>
              <L2KText variant="secondary" mb="lg">
                Browse our collection of urbankiz videos and begin learning today.
              </L2KText>
              <Link
                href="/videos"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
                  borderRadius: "8px",
                  color: "#fff",
                  textDecoration: "none",
                  fontFamily: "'Public Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Browse Videos <IconArrowRight size={16} />
              </Link>
            </Paper>
          )}
        </Stack>
      </PageContainer>
    </>
  );
}

// Per-page layout (Inertia pattern)
Dashboard.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout hideOnScroll={false}>{page}</AuthenticatedLayout>
);
